<template>
  <MTooltip
    tooltip-class="hidden lg:block"
    placement="bottom-end"
    no-arrow
    @open="scoreTooltipOpen"
    @close="scoreTooltipClose"
  >
    <template #toggle>
      <div class="flex cursor-default">
        <ScoreCircle
          :score="provider.score ? provider.score.value : undefined"
          size="sm"
          class="relative -mt-6 -mr-6 lg:mr-0 lg:-ml-6 shrink-0 shadow-1"
        />
        <p class="hidden lg:block ml-2 mt-2 text-14 underline decoration-dotted">
          {{ $t('monito_score') }}
        </p>
      </div>
    </template>
    <template #content>
      <div v-if="provider.score" class="h-16 w-32">
        <BackgroundImage
          class="h-full"
          size="contain"
          :src="provider.logo.sm"
          :alt="provider.displayName"
        />
      </div>
      <ScoreDetails :score="provider.score" :review-link="provider.url" />
    </template>
  </MTooltip>
</template>

<script lang="ts" setup>
import { panelOpen } from './analytics/actions'
import type { Provider } from './types'
import ScoreDetails from './score-details.vue'
import { Comparison } from './analytics/objects/Comparison'
import { ComparisonSection } from './analytics/objects/ComparisonSection'
import { ComparisonResult } from './analytics/objects/ComparisonResult'

const props = defineProps<{
  provider: Provider
}>()

const { $track } = useNuxtApp()

const getResultAnalyticsObj = inject('getResultAnalyticsObj') as (
  slug: string,
  location?: string,
) => ComparisonResult | undefined
const sectionAnalyticsObj = inject<Ref<ComparisonSection>>('sectionAnalyticsObj')
const comparisonAnalyticsObj = inject<Ref<Comparison>>('comparisonAnalyticsObj')

const scoreTooltipTimer = ref<ReturnType<typeof setTimeout> | null>(null)

function scoreTooltipOpen() {
  if (scoreTooltipTimer.value) {
    clearTimeout(scoreTooltipTimer.value)
  }
  scoreTooltipTimer.value = setTimeout(() => {
    panelOpen(
      $track,
      'Monito Score Breakdown',
      comparisonAnalyticsObj?.value,
      sectionAnalyticsObj?.value,
      getResultAnalyticsObj(props.provider.slug),
    ).catch(() => {
      // do nothing
    })
  }, 1500)
}

function scoreTooltipClose() {
  if (scoreTooltipTimer.value) {
    clearTimeout(scoreTooltipTimer.value)
    scoreTooltipTimer.value = null
  }
}
</script>
